/**
 * Delivery lead time — how soon a customer may ask for a box.
 *
 * Chapter 10 (F8). The checkout's delivery field was a bare `<input type="date">`
 * with no `min`, so a client could request tomorrow. Reported as: *"clients can
 * choose delivery options for tomorrow, but this is of course not do-able. We
 * need a minimum of 1,5 weeks (preferably 2 weeks) … And if they need anything
 * earlier, contact us."*
 *
 * Owner decision: **14 days, configurable per box.**
 *
 * This lives in `content-schema` so that the picker's floor and the server's
 * rejection are computed by the same code. If the client and the server derived
 * "today" independently they would disagree, and the UI would offer dates the
 * API then refused.
 */

/** Fallback when neither the box nor the site content specifies a lead time. */
export const DEFAULT_MIN_LEAD_DAYS = 14;

/**
 * The company's timezone. Lead time is a business rule counted in the seller's
 * calendar days, not the buyer's — a customer in Tokyo and one in Lisbon must
 * get the same floor.
 */
export const BUSINESS_TIMEZONE = 'Europe/Amsterdam';

/** `en-CA` renders as `YYYY-MM-DD`, which is what `<input type="date">` wants. */
const BUSINESS_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The effective lead time: the box's own value, else the site-wide default,
 * else 14.
 *
 * A non-positive stored value is ignored. Zero would mean "deliver today",
 * which is the very bug this module exists to prevent, and is far more likely
 * to be an authoring slip than an intention.
 */
export function resolveMinLeadDays(
  flow?: { minLeadDays?: number } | null,
  defaults?: { minLeadDays?: number } | null,
): number {
  const candidates = [flow?.minLeadDays, defaults?.minLeadDays];
  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return Math.floor(value);
    }
  }
  return DEFAULT_MIN_LEAD_DAYS;
}

/** Today's calendar date in the company timezone, as `YYYY-MM-DD`. */
export function businessToday(now: Date = new Date()): string {
  return BUSINESS_DATE.format(now);
}

/** Add whole calendar days to a `YYYY-MM-DD` string. */
function addDays(date: string, days: number): string {
  const parts = date.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  // Arithmetic at UTC midnight: a local-time `Date` would gain or lose an hour
  // across a DST boundary and can land a day early.
  return new Date(Date.UTC(y, m - 1, d) + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The earliest date the picker may offer — `min` on the input.
 *
 * Strict: exactly `today + minLeadDays`. Compare with
 * `isDeliveryDateAcceptable`, which is deliberately one day more forgiving.
 */
export function earliestDeliveryDate(
  minLeadDays: number = DEFAULT_MIN_LEAD_DAYS,
  now: Date = new Date(),
): string {
  return addDays(businessToday(now), minLeadDays);
}

/**
 * Server-side gate. `true` when this date may be accepted.
 *
 * Two deliberate asymmetries with `earliestDeliveryDate`:
 *
 * 1. **An absent date passes.** `deliveryDate` is optional on the submission
 *    and Chapter 10 does not make it mandatory — that would be a new
 *    requirement and would block customers who have no date in mind yet. Only
 *    a date that is given and is too soon is refused.
 * 2. **One day of grace.** A page loaded at 23:59 offers `today + lead`; if the
 *    customer presses send at 00:01 the server's "today" has advanced and would
 *    reject the date the UI had just offered. The slack closes that race and
 *    cannot breach the stated rule — the owner asked for "minimum 1,5 weeks,
 *    preferably 2", and 13 days is still more than 1,5 weeks.
 */
export function isDeliveryDateAcceptable(
  date: string | undefined | null,
  minLeadDays: number = DEFAULT_MIN_LEAD_DAYS,
  now: Date = new Date(),
): boolean {
  if (!date) return true;
  if (!ISO_DATE.test(date)) return false;
  // Reject a well-shaped but impossible date such as 2026-13-45.
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) return false;
  return date >= addDays(earliestDeliveryDate(minLeadDays, now), -1);
}
